import { Button } from '@miq/design-system/components/ui/button';
import { Input } from '@miq/design-system/components/ui/input';
import { Label } from '@miq/design-system/components/ui/label';

import type { DayRange, DayRangeQuery } from '../data/acrossCustomers';
import { dayRangeLabel } from '../data/format';

/**
 * Which run of calendar days this screen is answering for.
 *
 * Two dates, both inclusive, both UTC calendar days — the day a cost roll-up
 * is keyed on — and three presets that set the two dates and nothing else.
 * One control for the whole screen: the total beside each instance, the
 * day-by-day drill-down beneath it and the CSV export are all cut on this
 * range, so changing it changes all three together. A screen with a picker
 * that moved one figure and not the others was the thing this replaces.
 *
 * The selection is `null` until somebody asks. `null` is "did not ask", which
 * the service reads as the month to date, so a session left open across
 * midnight on the 1st follows the service rather than pinning the month it was
 * opened in — the same reasoning as `MonthPicker`. **This month** therefore
 * selects `null` rather than today's dates; the inputs show the days the
 * service answered with, which are the same days.
 *
 * The caption states the range the figures were actually cut on, taken from
 * the service's echo rather than recomputed here, so it cannot drift from the
 * arithmetic it describes. While nothing has been answered yet there is no
 * caption, because there is nothing true to say.
 *
 * Standalone rather than part of one page because Fleet Revenue mounts the
 * same control: two staff screens that choose days two different ways would
 * be two screens an operator cannot reconcile.
 */
export function DayRangePicker({
  selected,
  onSelect,
  range,
}: {
  selected: DayRangeQuery | null;
  onSelect: (range: DayRangeQuery | null) => void;
  /** The range the figures below were cut on, or `null` before the service has answered. */
  range: DayRange | null;
}) {
  const today = utcToday();
  const shown: DayRangeQuery | null = selected ?? (range === null ? null : { from: range.first, to: range.last });

  const change = (which: keyof DayRangeQuery, value: string) => {
    // A date input reports "" while a date is half-typed or cleared. That is
    // not a day and not a request: the range stays as it was until there is a
    // whole day to ask with.
    if (value === '' || shown === null) {
      return;
    }
    onSelect({ ...shown, [which]: value });
  };

  return (
    <div className="shrink-0 space-y-2 text-right">
      <div className="flex flex-wrap justify-end gap-1.5">
        <Button variant="outline" size="sm" onPress={() => onSelect(null)}>
          This month
        </Button>
        <Button variant="outline" size="sm" onPress={() => onSelect(previousMonth(today))}>
          Previous month
        </Button>
        <Button variant="outline" size="sm" onPress={() => onSelect(lastSevenDays(today))}>
          Last 7 days
        </Button>
      </div>
      <div className="flex flex-wrap items-end justify-end gap-3">
        <div className="space-y-1.5 text-left">
          <Label htmlFor="fleet-costs-from" className="text-xs text-muted-foreground">
            From
          </Label>
          <Input
            id="fleet-costs-from"
            type="date"
            className="w-40"
            value={shown?.from ?? ''}
            max={shown?.to ?? isoDay(today)}
            onChange={(event) => change('from', event.target.value)}
          />
        </div>
        <div className="space-y-1.5 text-left">
          <Label htmlFor="fleet-costs-to" className="text-xs text-muted-foreground">
            To
          </Label>
          <Input
            id="fleet-costs-to"
            type="date"
            className="w-40"
            value={shown?.to ?? ''}
            min={shown?.from}
            max={isoDay(today)}
            onChange={(event) => change('to', event.target.value)}
          />
        </div>
      </div>
      {range !== null && <div className="text-xs text-muted-foreground">{dayRangeLabel(range)}</div>}
    </div>
  );
}

/**
 * Today as a UTC calendar day.
 *
 * UTC on purpose: a roll-up is keyed on the UTC day, and an operator west of
 * Greenwich checking the screen in their evening is already on tomorrow's
 * UTC day — a preset cut on their local date would ask for a day the service
 * refuses as not having happened.
 */
function utcToday(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

/** The whole of the month before the one `today` is in. */
function previousMonth(today: Date): DayRangeQuery {
  const first = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
  const last = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0));
  return { from: isoDay(first), to: isoDay(last) };
}

/**
 * The seven closed days before `today`.
 *
 * Closed days, because an instance posts a day once it has ended: today is
 * almost never posted yet, and a "last 7 days" that included it would read
 * "6 of 7 days posted" every day of the year.
 */
function lastSevenDays(today: Date): DayRangeQuery {
  return { from: isoDay(daysBefore(today, 7)), to: isoDay(daysBefore(today, 1)) };
}

function daysBefore(day: Date, count: number): Date {
  return new Date(day.getTime() - count * 24 * 60 * 60 * 1000);
}

/** A UTC moment as the `YYYY-MM-DD` the wire and the date input both speak. */
function isoDay(day: Date): string {
  return day.toISOString().slice(0, 10);
}
